import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';

import SocialAccountsModal from '../components/social-accounts-modal/SocialAccountsModal';
import connect from '../utils/connect';

import sendNotification from '../actions/notifications/send';
import extendToken from '../actions/settings/extendToken';
import getFacebookLinkedAccounts from '../actions/settings/getFacebookLinkedAccounts';
import getInstagramLinkedAccounts from '../actions/settings/getInstagramLinkedAccounts';
import refreshFacebookPages from '../actions/settings/refreshFacebookPages';
import refreshInstagramAccounts from '../actions/settings/refreshInstagramAccounts';
import saveFbPages from '../actions/settings/saveFbPages';
import saveIgAccounts from '../actions/settings/saveIgAccounts';
import removeIgAccount from '../actions/settings/removeIgAccount';
import removeFbPage from '../actions/settings/removeFbPage';
import getClientRsps from '../actions/settings/getClientRsps';
import combineActions from '../utils/combineActions';
import prepareRenderList from '../utils/prepareRenderList';

import { Button, LoadingDots } from '@app/ui';

import logEvent from '../utils/logAuthEvents';

import '../styles/custom-myles-styles.scss';
import SocialMediaLinkageCard from './SocialMediaLinkageCard';
import { fbSdkURL } from '../utils/apiUrls';
import { Notification } from '@app/components/notifications/Notifications';

const scopes =
    'pages_show_list,read_insights,pages_read_engagement,pages_read_user_content,ads_management,business_management';

interface FacebookManagerProps {
    client: { id: string };
    socialAccounts: any;
    triggerModalChange: (modal: string | null) => void;
    login: (
        scopes: string,
        onSuccess: (token: string) => void,
        onFailure: () => void,
        type: string,
    ) => void;
    modal: string | null;
    onCloseModal: () => void;
    getFacebookLinkedAccounts: (clientId: string) => void;
    saveFbPages: (clientId: string, account: any) => Promise<void>;
    refreshFacebookPages: (fbPages: any) => void;
    removeFbPage: (
        clientId: string,
        accountId: string,
        platformUserId: string,
    ) => Promise<void>;
    sendNotification: (
        message: string,
        configs?: Notification['options'],
    ) => void;
}

const FacebookManager = ({
    client,
    socialAccounts,
    triggerModalChange,
    login,
    modal,
    onCloseModal,
    getFacebookLinkedAccounts,
    saveFbPages,
    refreshFacebookPages,
    removeFbPage,
    sendNotification,
}: FacebookManagerProps) => {
    const { t } = useTranslation('settings');
    const [isSubmittingAccount, setIsSubmittingAccount] = useState(false);

    useEffect(() => {
        getFacebookLinkedAccounts(client.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    const onLoginSuccess = async (token: string) => {
        // Use the token passed from the parent component instead of relying on Redux state
        const accessToken = token;

        const batch = encodeURI(
            JSON.stringify([
                {
                    method: 'GET',
                    relative_url:
                        'me?fields=id,name,accounts.limit(100){access_token,id,name,picture,page_token,username,link}',
                },
            ]),
        );

        const response = await fetch(
            `${fbSdkURL}/me?limit=100&access_token=${accessToken}&batch=${batch}`,
            {
                method: 'POST',
            },
        );
        const responseArray = await response.json();

        if (responseArray && responseArray.length > 0) {
            responseArray.forEach(({ body }: any) => {
                const data = JSON.parse(body);
                // eslint-disable-next-line no-prototype-builtins
                if (
                    data &&
                    Object.prototype.hasOwnProperty.call(data, 'accounts')
                ) {
                    const fbPages = pullFacebookPages(data);
                    refreshFacebookPages(fbPages);

                    logEvent({
                        type: 'link-list-all-available-accounts',
                        payload: {
                            platformId: 3,
                            platformName: 'facebook',
                            pages: fbPages,
                        },
                    });
                }
            });

            triggerModalChange('pick-facebook-page');
        }
    };

    const onLoginFailure = () => {
        alert(t('clientConfiguration.social.notifications.genericError'));

        logEvent({
            type: 'link-request-to-3rd-party-failed',
            payload: {
                platformId: 3,
                platformName: 'facebook',
            },
        });
    };

    const onUnlink = async (page: any) => {
        const { id: accountId, platformUserId } = page;
        await removeFbPage(client.id, accountId, platformUserId);

        logEvent({
            type: 'unlink',
            payload: {
                accountId,
                platformUserId,
                platformId: 3,
                platformName: 'facebook',
            },
        });
    };

    const onSubmit = async (selectedPages: string[]) => {
        setIsSubmittingAccount(true);

        try {
            const { pages, accessToken: userAccessToken } =
                socialAccounts.facebook;

            const clientAuthorizedAccounts = selectedPages.map(
                platformUserId => {
                    const page = pages[platformUserId];

                    return {
                        clientId: client.id,
                        account: page,
                        accessToken: page.token.accessToken,
                        platformUserId: page.userId,
                        userAccessToken,
                    };
                },
            );

            await saveFbPages(client.id, clientAuthorizedAccounts);

            logEvent({
                type: 'link-selected-accounts',
                payload: {
                    pages: selectedPages,
                    platformId: 3,
                    platformName: 'facebook',
                },
            });

            triggerModalChange(null);
            sendNotification(
                t(
                    'clientConfiguration.social.notifications.demographicPopulate',
                ),
                {
                    description: t(
                        'clientConfiguration.social.notifications.impressionPopulate',
                    ),
                },
            );
        } catch (error: any) {
            if (!error?.wasHandledByApi) {
                sendNotification(
                    t('clientConfiguration.social.notifications.genericError'),
                    { context: 'danger' },
                );
            }
        } finally {
            setIsSubmittingAccount(false);
        }
    };

    const onLogin = () => {
        login(scopes, onLoginSuccess, onLoginFailure, 'facebook');

        logEvent({
            type: 'link-request-to-3rd-party',
            payload: {
                platformId: 3,
                platformName: 'facebook',
            },
        });
    };

    const linkedPagesKeys = Object.keys(socialAccounts.facebook.linkedPages);
    const facebookAccounts = Object.values(
        socialAccounts.facebook.linkedPages,
    ).map((account: any) => {
        if (account.name) return account;
        account.name = account.username;
        return account;
    });
    const renderList = prepareRenderList(facebookAccounts);

    return (
        <div className="_flex-card">
            <div className="gds-card -m-b-0">
                {linkedPagesKeys.length > 5 && <span className="-shadow" />}

                <div className="gds-card__block gds-card__block--divide-bottom -p-h-3">
                    <p className="gds-text--regular">
                        <i className="fa fa-facebook-square -m-r-2" />
                        {t('clientConfiguration.social.facebook.title')}
                    </p>
                </div>
                <div className="gds-card__block -p-h-3 -p-t-3 -p-b-1 -overflow-y-scroll -social-account-container">
                    <div
                        className="gds-flex gds-flex--justify-center"
                        style={{ height: '100%' }}
                    >
                        {socialAccounts.facebook.isRequestingLinkedPages && (
                            <LoadingDots />
                        )}

                        {!socialAccounts.facebook.isRequestingLinkedPages &&
                            linkedPagesKeys.length > 0 && (
                                <div
                                    className="gds-flex gds-flex--justify-center -m-b-2"
                                    style={{ width: '100%' }}
                                >
                                    <Button
                                        onClick={onLogin}
                                        size="small"
                                        variant="primary"
                                        style={{ width: '100%' }}
                                    >
                                        {t(
                                            'clientConfiguration.social.facebook.linkAnother',
                                        )}
                                    </Button>
                                </div>
                            )}

                        {!socialAccounts.facebook.isRequestingLinkedPages &&
                            renderList.map((clientAuthorizedAccount: any) => {
                                const account = clientAuthorizedAccount.account;
                                const revoked = clientAuthorizedAccount.revoked;

                                const id = account.id;
                                const name = account.name;
                                const platformUserId = account.platformUserId;

                                const accountLogo = `https://graph.facebook.com/${platformUserId}/picture`;

                                const unlink = () => onUnlink(account);

                                return (
                                    <SocialMediaLinkageCard
                                        key={id}
                                        type="facebook"
                                        logo={accountLogo}
                                        revoked={revoked}
                                        _link={onLogin}
                                        _unlink={unlink}
                                        name={name}
                                    />
                                );
                            })}

                        {!socialAccounts.facebook.isRequestingLinkedPages &&
                            linkedPagesKeys.length === 0 && (
                                <Button
                                    onClick={onLogin}
                                    size="small"
                                    variant="primary"
                                    style={{ width: '100%' }}
                                >
                                    {t(
                                        'clientConfiguration.social.facebook.link',
                                    )}
                                </Button>
                            )}
                    </div>
                </div>
            </div>
            {modal === 'pick-facebook-page' && (
                <SocialAccountsModal
                    onCloseModal={onCloseModal}
                    items={socialAccounts.facebook.pages}
                    linkedItems={linkedPagesKeys}
                    modal={modal}
                    onSubmit={onSubmit}
                    isSubmitting={isSubmittingAccount}
                />
            )}
        </div>
    );
};

const pullFacebookPages = (response: any) => {
    const {
        accounts: { data: fbPages },
        id: userId,
    } = response;
    return fbPages.reduce((acc: any, currVal: any) => {
        const { access_token: accessToken, id, name, link } = currVal;
        const picture = R.path(['picture', 'data', 'url'], currVal) || null;
        let username = R.path(['username'], currVal) || null;
        if (!username) {
            username = R.path(['page_token'], currVal) || null;
        }

        const pageData = {
            platformUserId: id,
            userId,
            platformId: 3,
            username,
            name,
            profilePictureUrl: picture,
            type: 'facebook_page',
            id: null,
            profileUrl: link,
            addedAt: null,
            token: {
                accessToken,
                tokenType: 'bearer',
                expire: null,
                revoked: false,
            },
        };
        acc[id] = pageData;
        return acc;
    }, {});
};

export default connect(
    ['socialAccounts', 'client'],
    combineActions({
        sendNotification,
        extendToken,
        getFacebookLinkedAccounts,
        getInstagramLinkedAccounts,
        refreshFacebookPages,
        refreshInstagramAccounts,
        saveFbPages,
        saveIgAccounts,
        removeIgAccount,
        removeFbPage,
        getClientRsps,
    }),
)(FacebookManager);
